import React, { useEffect, useState } from 'react';
import { Plus, Edit, Trash2, Grid, List, Building2, Calendar, Clock, FileText, User, ChevronUp, ChevronDown } from 'lucide-react';
import { tenantsAPI } from '../services/tenantsAPI';
import { roomsAPI } from '../services/api';
import { formatDate } from '../utils/helpers';
import toast from 'react-hot-toast';
import DeleteConfirmModal from '../components/DeleteConfirmModal';

const calculateAge = (dob) => {
  if (!dob) return '—';
  const birth = new Date(dob);
  if (isNaN(birth)) return '—';
  const today = new Date();
  let years = today.getFullYear() - birth.getFullYear();
  const m = today.getMonth() - birth.getMonth();
  if (m < 0 || (m === 0 && today.getDate() < birth.getDate())) years--;
  return `${years} yrs`;
};

const calculateTenancy = (leaseStart) => {
  if (!leaseStart) return '—';
  const start = new Date(leaseStart);
  if (isNaN(start)) return '—';
  const today = new Date();
  let years = today.getFullYear() - start.getFullYear();
  let months = today.getMonth() - start.getMonth();
  if (today.getDate() < start.getDate()) months--;
  if (months < 0) { years--; months += 12; }
  if (years === 0) return `${months} mo`;
  if (months === 0) return `${years} yr${years !== 1 ? 's' : ''}`;
  return `${years} yr${years !== 1 ? 's' : ''} ${months} mo`;
};

const TenantsPage = () => {
  const [tenants, setTenants] = useState([]);
  const [rooms, setRooms] = useState([]);
  const [selectedRoom, setSelectedRoom] = useState('all');
  const [sortConfig, setSortConfig] = useState({ key: null, direction: 'asc' });
  const [loading, setLoading] = useState(true);
  const [viewMode, setViewMode] = useState('list');
  const [showModal, setShowModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [tenantToDelete, setTenantToDelete] = useState(null);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ full_name: '', date_of_birth: '', lease_start: '', room_id: '' });
  const [files, setFiles] = useState({ passport: null, id_card: null, other_doc: null });

  const fetchTenants = async () => {
    try {
      setLoading(true);
      const res = await tenantsAPI.getAll();
      setTenants(res.data.tenants || []);
    } catch (error) {
      console.error(error);
      toast.error('Failed to load tenants');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchTenants(); }, []);

  useEffect(() => {
    const fetchRooms = async () => {
      try {
        const res = await roomsAPI.getAll();
        setRooms(res.data.rooms || []);
      } catch (error) {
        console.error('Failed to load rooms', error);
      }
    };
    fetchRooms();
  }, []);

  const sortedAndFilteredTenants = React.useMemo(() => {
    let list = tenants.slice();
    if (selectedRoom && selectedRoom !== 'all') {
      list = list.filter(t => String(t.room_id) === String(selectedRoom));
    }
    if (sortConfig.key) {
      list.sort((a, b) => {
        const aVal = a[sortConfig.key] ?? '';
        const bVal = b[sortConfig.key] ?? '';
        if (typeof aVal === 'string' && typeof bVal === 'string') {
          const res = aVal.localeCompare(bVal, undefined, { numeric: true, sensitivity: 'base' });
          return sortConfig.direction === 'asc' ? res : -res;
        }
        if (aVal < bVal) return sortConfig.direction === 'asc' ? -1 : 1;
        if (aVal > bVal) return sortConfig.direction === 'asc' ? 1 : -1;
        return 0;
      });
    }
    return list;
  }, [tenants, selectedRoom, sortConfig]);

  const renderSortIcon = (key) => {
    if (sortConfig.key !== key) return null;
    return sortConfig.direction === 'asc' ? (
      <ChevronUp className="w-3 h-3 inline-block ml-2" />
    ) : (
      <ChevronDown className="w-3 h-3 inline-block ml-2" />
    );
  };

  const openModal = (tenant = null) => {
    if (tenant) {
      setEditing(tenant);
      setForm({ full_name: tenant.full_name || '', date_of_birth: tenant.date_of_birth || '', lease_start: tenant.lease_start || '', room_id: tenant.room_id || '' });
    } else {
      setEditing(null);
      setForm({ full_name: '', date_of_birth: '', lease_start: '', room_id: '' });
      setFiles({ passport: null, id_card: null, other_doc: null });
    }
    setShowModal(true);
  };

  const closeModal = () => setShowModal(false);

  const handleChange = (e) => setForm(prev => ({ ...prev, [e.target.name]: e.target.value }));

  const handleFile = (e) => {
    const { name, files } = e.target;
    setFiles(prev => ({ ...prev, [name]: files[0] }));
  };

  const submit = async (e) => {
    e.preventDefault();
    try {
      const fd = new FormData();
      fd.append('full_name', form.full_name);
      fd.append('date_of_birth', form.date_of_birth || '');
      fd.append('lease_start', form.lease_start || '');
      fd.append('room_id', form.room_id || '');
      if (files.passport) fd.append('passport', files.passport);
      if (files.id_card) fd.append('id_card', files.id_card);
      if (files.other_doc) fd.append('other_doc', files.other_doc);

      if (editing) {
        await tenantsAPI.update(editing.id, fd);
        toast.success('Tenant updated');
      } else {
        await tenantsAPI.create(fd);
        toast.success('Tenant created');
      }
      closeModal();
      fetchTenants();
    } catch (error) {
      console.error(error);
      toast.error('Failed to save tenant');
    }
  };

  const remove = (tenant) => {
    setTenantToDelete(tenant);
    setShowDeleteModal(true);
  };

  const confirmDelete = async () => {
    try {
      await tenantsAPI.remove(tenantToDelete.id);
      toast.success('Tenant removed');
      fetchTenants();
    } catch (error) {
      console.error(error);
      toast.error('Failed to delete tenant');    }
  };

  const handleOpenDoc = async (filename) => {
    try {
      const response = await tenantsAPI.downloadFile(filename);
      const url = URL.createObjectURL(response.data);
      const tab = window.open(url, '_blank', 'noreferrer');
      if (!tab) toast.error('Please allow pop-ups to view documents');
      setTimeout(() => URL.revokeObjectURL(url), 15000);
    } catch (err) {
      toast.error('Failed to open document');
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-secondary-900 dark:text-secondary-100">Tenants</h1>
          <p className="text-secondary-600 dark:text-secondary-300">Manage tenant records and documents</p>
        </div>
        <div className="flex items-center space-x-3">
          {/* View Toggle (same as MetersPage) */}
          <div className="flex rounded-lg overflow-hidden border border-secondary-300">
            <button
              onClick={() => setViewMode('list')}
              className={`px-3 py-2 flex items-center space-x-2 transition-colors ${
                viewMode === 'list'
                  ? 'bg-primary-500 text-white'
                  : 'bg-white dark:bg-secondary-800 text-secondary-600 dark:text-secondary-300 hover:bg-secondary-50 dark:hover:bg-secondary-700'
              }`}
            >
              <List className="w-4 h-4" />
              <span className="hidden sm:inline">List</span>
            </button>
            <button
              onClick={() => setViewMode('grid')}
              className={`px-3 py-2 flex items-center space-x-2 transition-colors ${
                viewMode === 'grid'
                  ? 'bg-primary-500 text-white'
                  : 'bg-white dark:bg-secondary-800 text-secondary-600 dark:text-secondary-300 hover:bg-secondary-50 dark:hover:bg-secondary-700'
              }`}
            >
              <Grid className="w-4 h-4" />
              <span className="hidden sm:inline">Grid</span>
            </button>
          </div>
          <button onClick={() => openModal()} className="btn-primary">
            <Plus className="w-4 h-4 mr-2" />
            <span className="hidden sm:inline">Add Tenant</span>
          </button>
        </div>
      </div>

      {loading ? (
        <div className="text-center py-12">Loading...</div>
      ) : (
        <div>
          {tenants.length === 0 ? (
            <div className="text-center py-12">No tenants found</div>
          ) : viewMode === 'list' ? (
            <div className="bg-white dark:bg-secondary-800 rounded-2xl shadow-sm border border-secondary-200 dark:border-secondary-700 overflow-hidden">
              <div className="px-4 py-3 flex items-center justify-between gap-4">
                <div className="flex items-center gap-3">
                  <label className="text-sm text-secondary-600 dark:text-secondary-300">Filter:</label>
                  <select value={selectedRoom} onChange={(e) => setSelectedRoom(e.target.value)} className="px-3 py-1 border rounded">
                    <option value="all">All rooms</option>
                    {rooms.map(r => (
                      <option key={r.id} value={r.id}>{r.number || r.name || `Room ${r.id}`}</option>
                    ))}
                  </select>
                </div>
                <div className="text-sm text-secondary-500">Showing {sortedAndFilteredTenants.length} tenants</div>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="bg-secondary-50 dark:bg-secondary-700/50 border-b border-secondary-200 dark:border-secondary-700">
                      <th onClick={() => setSortConfig(prev => ({ key: 'full_name', direction: prev.key === 'full_name' && prev.direction === 'asc' ? 'desc' : 'asc' }))}
                        className="cursor-pointer text-left px-5 py-3.5 text-xs font-semibold text-secondary-500 dark:text-secondary-400 uppercase tracking-wider">
                        Tenant {renderSortIcon('full_name')}
                      </th>
                      <th onClick={() => setSortConfig(prev => ({ key: 'room_number', direction: prev.key === 'room_number' && prev.direction === 'asc' ? 'desc' : 'asc' }))}
                        className="cursor-pointer text-left px-5 py-3.5 text-xs font-semibold text-secondary-500 dark:text-secondary-400 uppercase tracking-wider">
                        Room {renderSortIcon('room_number')}
                      </th>
                      <th onClick={() => setSortConfig(prev => ({ key: 'date_of_birth', direction: prev.key === 'date_of_birth' && prev.direction === 'asc' ? 'desc' : 'asc' }))}
                        className="cursor-pointer text-left px-5 py-3.5 text-xs font-semibold text-secondary-500 dark:text-secondary-400 uppercase tracking-wider">
                        Date of Birth {renderSortIcon('date_of_birth')}
                      </th>
                      <th onClick={() => setSortConfig(prev => ({ key: 'date_of_birth', direction: prev.key === 'date_of_birth' && prev.direction === 'asc' ? 'desc' : 'asc' }))}
                        className="cursor-pointer text-left px-5 py-3.5 text-xs font-semibold text-secondary-500 dark:text-secondary-400 uppercase tracking-wider">
                        Age {renderSortIcon('date_of_birth')}
                      </th>
                      <th onClick={() => setSortConfig(prev => ({ key: 'lease_start', direction: prev.key === 'lease_start' && prev.direction === 'asc' ? 'desc' : 'asc' }))}
                        className="cursor-pointer text-left px-5 py-3.5 text-xs font-semibold text-secondary-500 dark:text-secondary-400 uppercase tracking-wider">
                        Lease Start {renderSortIcon('lease_start')}
                      </th>
                      <th onClick={() => setSortConfig(prev => ({ key: 'lease_start', direction: prev.key === 'lease_start' && prev.direction === 'asc' ? 'desc' : 'asc' }))}
                        className="cursor-pointer text-left px-5 py-3.5 text-xs font-semibold text-secondary-500 dark:text-secondary-400 uppercase tracking-wider">
                        Tenancy {renderSortIcon('lease_start')}
                      </th>
                      <th className="text-left px-5 py-3.5 text-xs font-semibold text-secondary-500 dark:text-secondary-400 uppercase tracking-wider">Documents</th>
                      <th className="text-left px-5 py-3.5 text-xs font-semibold text-secondary-500 dark:text-secondary-400 uppercase tracking-wider">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-secondary-100 dark:divide-secondary-700">
                    {sortedAndFilteredTenants.map(t => {
                      const initials = t.full_name.split(' ').map(n => n[0]).join('').slice(0, 2).toUpperCase();
                      return (
                        <tr key={t.id} className="hover:bg-secondary-50 dark:hover:bg-secondary-700/30 transition-colors group">
                          {/* Tenant with avatar */}
                          <td className="px-5 py-4">
                            <div className="flex items-center gap-3">
                              <div className="w-9 h-9 rounded-full bg-gradient-to-br from-primary-400 to-primary-600 flex items-center justify-center flex-shrink-0">
                                <span className="text-white text-xs font-bold">{initials}</span>
                              </div>
                              <span className="font-medium text-secondary-900 dark:text-secondary-100">{t.full_name}</span>
                            </div>
                          </td>
                          {/* Room */}
                          <td className="px-5 py-4">
                            {t.room_number ? (
                              <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium bg-primary-100 dark:bg-primary-900/40 text-primary-700 dark:text-primary-300">
                                <Building2 className="w-3 h-3" />{t.room_number}
                              </span>
                            ) : (
                              <span className="text-secondary-400 dark:text-secondary-500 text-xs">—</span>
                            )}
                          </td>
                          {/* DOB */}
                          <td className="px-5 py-4 text-secondary-600 dark:text-secondary-300">{(formatDate(t.date_of_birth, 'dd-MMM-yyyy')) || <span className="text-secondary-400">—</span>}</td>
                          {/* Age */}
                          <td className="px-5 py-4">
                            <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-lg text-xs font-semibold bg-secondary-100 dark:bg-secondary-700 text-secondary-700 dark:text-secondary-300">
                              <User className="w-3 h-3" />{calculateAge(t.date_of_birth)}
                            </span>
                          </td>
                          {/* Lease Start */}
                          <td className="px-5 py-4 text-secondary-600 dark:text-secondary-300">{(formatDate(t.lease_start, 'dd-MMM-yyyy')) || <span className="text-secondary-400">—</span>}</td>
                          {/* Tenancy */}
                          <td className="px-5 py-4">
                            <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-lg text-xs font-semibold bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300">
                              <Clock className="w-3 h-3" />{calculateTenancy(t.lease_start)}
                            </span>
                          </td>
                          {/* Documents */}
                          <td className="px-5 py-4">
                            <div className="flex flex-wrap gap-1.5">
                              {t.passport_path && (
                                <button onClick={() => handleOpenDoc(t.passport_path)}
                                  className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium bg-blue-50 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 hover:bg-blue-100 dark:hover:bg-blue-900/50 transition-colors">
                                  <FileText className="w-3 h-3" />Passport
                                </button>
                              )}
                              {t.id_card_path && (
                                <button onClick={() => handleOpenDoc(t.id_card_path)}
                                  className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium bg-green-50 dark:bg-green-900/30 text-green-700 dark:text-green-300 hover:bg-green-100 dark:hover:bg-green-900/50 transition-colors">
                                  <FileText className="w-3 h-3" />ID Card
                                </button>
                              )}
                              {t.other_doc_path && (
                                <button onClick={() => handleOpenDoc(t.other_doc_path)}
                                  className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium bg-purple-50 dark:bg-purple-900/30 text-purple-700 dark:text-purple-300 hover:bg-purple-100 dark:hover:bg-purple-900/50 transition-colors">
                                  <FileText className="w-3 h-3" />Other
                                </button>
                              )}
                              {!t.passport_path && !t.id_card_path && !t.other_doc_path && (
                                <span className="text-secondary-400 dark:text-secondary-500 text-xs italic">None</span>
                              )}
                            </div>
                          </td>
                          {/* Actions */}
                          <td className="px-5 py-4">
                            <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                              <button onClick={() => openModal(t)} title="Edit"
                                className="p-1.5 text-secondary-500 hover:text-primary-600 hover:bg-primary-50 dark:hover:bg-primary-900/30 rounded-lg transition-colors">
                                <Edit className="w-4 h-4" />
                              </button>
                              <button onClick={() => remove(t)} title="Delete"
                                className="p-1.5 text-secondary-500 hover:text-red-600 hover:bg-red-50 dark:hover:bg-red-900/30 rounded-lg transition-colors">
                                <Trash2 className="w-4 h-4" />
                              </button>
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {tenants.map(t => {
                const initials = t.full_name.split(' ').map(n => n[0]).join('').slice(0, 2).toUpperCase();
                const hasDoc = t.passport_path || t.id_card_path || t.other_doc_path;
                return (
                  <div key={t.id} className="bg-white dark:bg-secondary-800 rounded-2xl shadow-sm border border-secondary-200 dark:border-secondary-700 overflow-hidden hover:shadow-md transition-shadow duration-200">
                    {/* Card header with gradient */}
                    <div className="relative bg-gradient-to-br from-primary-500 to-primary-700 px-6 pt-6 pb-4">
                      <div className="flex justify-end space-x-1 absolute top-3 right-3">
                        <button
                          onClick={() => openModal(t)}
                          className="p-1.5 bg-white bg-opacity-20 hover:bg-opacity-30 text-white rounded-lg transition-colors"
                          title="Edit"
                        >
                          <Edit className="w-3.5 h-3.5" />
                        </button>
                        <button
                          onClick={() => remove(t)}
                          className="p-1.5 bg-white bg-opacity-20 hover:bg-red-500 hover:bg-opacity-80 text-white rounded-lg transition-colors"
                          title="Delete"
                        >
                          <Trash2 className="w-3.5 h-3.5" />
                        </button>
                      </div>
                      {/* Avatar + Name inside gradient */}
                      <div className="flex flex-col items-center gap-2">
                        <div className="w-16 h-16 rounded-full bg-white bg-opacity-20 border-2 border-white border-opacity-50 flex items-center justify-center flex-shrink-0">
                          <span className="text-white text-xl font-bold">{initials}</span>
                        </div>
                        <h3 className="text-white font-semibold text-center text-sm leading-snug px-2">{t.full_name}</h3>
                      </div>
                    </div>

                    {/* Room pill */}
                    <div className="px-6 pt-3 flex flex-col items-center">
                      {t.room_number ? (
                        <span className="inline-flex items-center gap-1 px-3 py-0.5 rounded-full text-xs font-medium bg-primary-100 dark:bg-primary-900 text-primary-700 dark:text-primary-300">
                          <Building2 className="w-3 h-3" />
                          Room {t.room_number}
                        </span>
                      ) : (
                        <span className="inline-flex items-center px-3 py-0.5 rounded-full text-xs font-medium bg-secondary-100 dark:bg-secondary-700 text-secondary-500 dark:text-secondary-400">
                          No room assigned
                        </span>
                      )}
                    </div>

                    {/* Stats row */}
                    <div className="mx-6 mt-4 grid grid-cols-2 gap-3">
                      <div className="bg-secondary-50 dark:bg-secondary-700 rounded-xl p-3 flex flex-col">
                        <span className="text-xs text-secondary-500 dark:text-secondary-400 flex items-center gap-1 mb-1">
                          <User className="w-3 h-3" /> Age
                        </span>
                        <span className="text-sm font-semibold text-secondary-900 dark:text-secondary-100">{calculateAge(t.date_of_birth)}</span>
                        <span className="text-xs text-secondary-400 dark:text-secondary-500 mt-0.5">{formatDate(t.date_of_birth, 'dd-MMM-yyyy') || 'N/A'}</span>
                      </div>
                      <div className="bg-secondary-50 dark:bg-secondary-700 rounded-xl p-3 flex flex-col">
                        <span className="text-xs text-secondary-500 dark:text-secondary-400 flex items-center gap-1 mb-1">
                          <Clock className="w-3 h-3" /> Tenancy
                        </span>
                        <span className="text-sm font-semibold text-secondary-900 dark:text-secondary-100">{calculateTenancy(t.lease_start)}</span>
                        <span className="text-xs text-secondary-400 dark:text-secondary-500 mt-0.5">
                          {t.lease_start ? `Since ${formatDate(t.lease_start, 'dd-MMM-yyyy')}` : 'N/A'}
                        </span>
                      </div>
                    </div>

                    {/* Documents footer */}
                    <div className="mx-6 mt-3 mb-5">
                      {hasDoc ? (
                        <div className="flex flex-wrap gap-2">
                          {t.passport_path && (
                            <button
                              onClick={() => handleOpenDoc(t.passport_path)}
                              className="inline-flex items-center gap-1 px-2.5 py-1 rounded-lg text-xs font-medium bg-blue-50 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 hover:bg-blue-100 dark:hover:bg-blue-900/50 transition-colors"
                            >
                              <FileText className="w-3 h-3" /> Passport
                            </button>
                          )}
                          {t.id_card_path && (
                            <button
                              onClick={() => handleOpenDoc(t.id_card_path)}
                              className="inline-flex items-center gap-1 px-2.5 py-1 rounded-lg text-xs font-medium bg-green-50 dark:bg-green-900/30 text-green-700 dark:text-green-300 hover:bg-green-100 dark:hover:bg-green-900/50 transition-colors"
                            >
                              <FileText className="w-3 h-3" /> ID Card
                            </button>
                          )}
                          {t.other_doc_path && (
                            <button
                              onClick={() => handleOpenDoc(t.other_doc_path)}
                              className="inline-flex items-center gap-1 px-2.5 py-1 rounded-lg text-xs font-medium bg-purple-50 dark:bg-purple-900/30 text-purple-700 dark:text-purple-300 hover:bg-purple-100 dark:hover:bg-purple-900/50 transition-colors"
                            >
                              <FileText className="w-3 h-3" /> Other
                            </button>
                          )}
                        </div>
                      ) : (
                        <p className="text-xs text-secondary-400 dark:text-secondary-500 italic">No documents uploaded</p>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      {/* Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-secondary-800 rounded-lg shadow-xl max-w-md w-full p-6 border border-secondary-700">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-secondary-900 dark:text-secondary-100">{editing ? 'Edit Tenant' : 'Add Tenant'}</h3>
              <button onClick={closeModal} className="p-2 hover:bg-secondary-100 rounded-lg transition-colors">✕</button>
            </div>
            <form onSubmit={submit} className="space-y-4">
              <div>
                <label className="form-label">Full name</label>
                <input name="full_name" value={form.full_name} onChange={handleChange} className="form-input" required />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="form-label">Date of birth</label>
                  <input type="date" name="date_of_birth" value={form.date_of_birth} onChange={handleChange} className="form-input" />
                </div>
                <div>
                  <label className="form-label">Lease start</label>
                  <input type="date" name="lease_start" value={form.lease_start} onChange={handleChange} className="form-input" />
                </div>
              </div>

              <div>
                <label className="form-label">Room (optional)</label>
                <select name="room_id" value={form.room_id} onChange={handleChange} className="form-input">
                  <option value="">-- Select Room --</option>
                  {rooms.map(r => (
                    <option key={r.id} value={r.id}>{r.room_number}</option>
                  ))}
                </select>
              </div>

              <div>
                <label className="form-label">Passport (PDF)</label>
                <input type="file" name="passport" accept="application/pdf" onChange={handleFile} className="form-input" />
              </div>

              <div>
                <label className="form-label">ID Card (PDF)</label>
                <input type="file" name="id_card" accept="application/pdf" onChange={handleFile} className="form-input" />
              </div>

              <div className="flex items-center justify-end space-x-3 pt-4">
                <button type="button" onClick={closeModal} className="btn-outline">Cancel</button>
                <button type="submit" className="btn-primary">{editing ? 'Update' : 'Create'}</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      <DeleteConfirmModal
        isOpen={showDeleteModal}
        onClose={() => setShowDeleteModal(false)}
        onConfirm={confirmDelete}
        itemName={tenantToDelete?.full_name || ''}
        itemType="tenant"
      />

    </div>
  );
};

export default TenantsPage;
